import { type AccountSummary, AnalyticsMembershipEvent } from '@fruitful/types';
import { AnimatedScrollView } from '@kanelloc/react-native-animated-header-scroll-view';
import { useFeatureGate } from '@statsig/expo-bindings';
import { useAtom, useAtomValue } from 'jotai';
import * as React from 'react';
import { useEffect, useState } from 'react';
import ConfettiCannon from 'react-native-confetti-cannon';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
    BackButton,
    BackButtonIconType,
    type ColorTokenKey,
    H6Emphasized,
    Icon,
    isWeb,
    LabelRegular,
    PageBelowHeader,
    Private,
    SafeAreaView,
    useThemeColors,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { isEmpty, sortByProperty } from '../../../common/misc';
import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import { useViewEvents } from '../../../events/hooks/useViewEvents';
import { FeatureFlags } from '../../../feature-flags/FeatureFlags';
import useGetHolisticSummary from '../../../holistic/hooks/useGetHolisticSummary';
import { holisticSummaryAtom } from '../../../holistic/state/HolisticAtoms';
import {
    captureException,
    captureMessage,
    trackForMembership,
} from '../../../logger/Logger';
import useUpdateGoalTask from '../../hooks/useUpdateGoalTask';
import { fireGoalConfettiCannonAtom } from '../../state/GoalAtoms';
import {
    GoalColors,
    GoalIcons,
    GoalSecondaryColors,
    GoalTaskStatus,
    type IGoal,
    type IGoalCategory,
    type IGoalTask,
} from '../../types/Goal';
import GoalBalanceButton from './GoalBalanceButton';
import GoalTarget from './GoalTarget';
import GoalTasks from './GoalTasks';
import LinkAccountButton from './LinkAccountButton';

interface GoalProps {
    goal: IGoal;
}

const Goal = ({ goal }: GoalProps) => {
    const [tasks, setTasks] = useState<IGoalTask[]>(goal.tasks ?? []);

    const { value: isGoalAccountLinkingEnabled } = useFeatureGate(
        FeatureFlags.ENABLE_GOAL_ACCOUNT_LINKING,
    );

    useEffect(() => {
        setTasks(goal.tasks ?? []);
    }, [goal]);

    const user = useAuthedUser();
    const membershipId = user.membership?.id ?? '';

    const holisticSummary = useAtomValue(holisticSummaryAtom);
    const { isLoading: isLoadingHolisticSummary } =
        useGetHolisticSummary(!holisticSummary);

    const { description } = goal;

    const activeTasks = tasks
        .filter((task) => task.status === GoalTaskStatus.IN_PROGRESS)
        .sort((a, b) => sortByProperty(a, b, 'position'));
    const completedTasks = tasks
        .filter((task) => task.status === GoalTaskStatus.COMPLETE)
        .sort((a, b) => sortByProperty(a, b, 'updatedAt'));
    const sortedTasks = [...activeTasks, ...completedTasks];

    const { mutate: updateGoalTask } = useUpdateGoalTask();

    const onTaskUpdate = (taskId: string, status: GoalTaskStatus) => {
        const newTasks = tasks.map((task) => {
            if (task.id === taskId) task.status = status;
            return task;
        });

        // optimistically update the task in the UI
        setTasks(newTasks);

        updateGoalTask(
            {
                urlParams: {
                    goalTaskId: taskId,
                },
                body: {
                    membershipId,
                    status,
                },
            },
            {
                onSuccess() {
                    if (
                        newTasks.every(
                            (task) => task.status === GoalTaskStatus.COMPLETE,
                        )
                    ) {
                        trackForMembership(
                            AnalyticsMembershipEvent.ALL_TASKS_COMPLETED_FOR_GOAL,
                            {
                                goalId: goal.id,
                                goalCategory: goal.category?.description ?? '',
                                goalCreatedAt: goal.createdAt,
                            },
                        );
                    }
                },
                onError(e) {
                    // TODO: decide if we should show any errors / revert state
                    captureMessage('Error updating goal task status');
                    captureException(e, { membershipId, taskId, status });
                },
            },
        );
    };

    const goalHasTargetAmount = goal.targetAmount != null;
    const goalHasLinkedAccounts = !isEmpty(goal.accountLinks);

    const holisticSummaryAccounts = Object.values(holisticSummary ?? {})
        .map((categorySummary) => categorySummary.accounts)
        .flat();

    const linkedAccounts: Record<string, AccountSummary> = (
        goal.accountLinks ?? []
    ).reduce((acc, accountLink) => {
        const foundHolisticAccount = holisticSummaryAccounts.find(
            (holisticSummaryAccount) =>
                holisticSummaryAccount.provider.toUpperCase() ===
                    accountLink.provider.toUpperCase() &&
                holisticSummaryAccount.id === accountLink.accountId,
        );

        if (foundHolisticAccount) {
            return { ...acc, [accountLink.id]: foundHolisticAccount };
        }

        return acc;
    }, {});

    return (
        <YStack
            gap={12}
            key={goal.id}
            width="100%"
            maxWidth={700}
            alignSelf="center"
        >
            <Private>
                <H6Emphasized flexShrink={1}>{description}</H6Emphasized>
            </Private>
            <XStack gap={16}>
                {goalHasLinkedAccounts && !isEmpty(linkedAccounts) && (
                    <GoalBalanceButton linkedAccounts={linkedAccounts} />
                )}
                {goalHasTargetAmount && (
                    <GoalTarget targetAmount={goal.targetAmount ?? 0} />
                )}
            </XStack>
            {isGoalAccountLinkingEnabled &&
                goalHasTargetAmount &&
                !isLoadingHolisticSummary &&
                !goalHasLinkedAccounts && (
                    <LinkAccountButton goalId={goal.id} />
                )}
            <GoalTasks
                goal={goal}
                tasks={sortedTasks}
                goalStatus={goal.status}
                onTaskUpdate={onTaskUpdate}
            />
        </YStack>
    );
};

interface HeaderProps {
    goalCategory: IGoalCategory | undefined;
}

const Header = ({ goalCategory }: HeaderProps) => {
    return (
        <SafeAreaView
            edges={['top', 'left', 'right']}
            style={{ width: '100%' }}
        >
            <XStack
                flex={1}
                alignItems="center"
                justifyContent="center"
                marginTop={60}
            >
                {goalCategory && (
                    <Icon
                        icon={GoalIcons[goalCategory.description]}
                        size={48}
                        color={GoalSecondaryColors[goalCategory.description]}
                    />
                )}
            </XStack>
        </SafeAreaView>
    );
};

interface TopNavBarComponentProps {
    height: number;
    isScrolled: boolean;
    title?: string;
}

const TopNavBarComponent = ({
    height,
    isScrolled,
    title = '',
}: TopNavBarComponentProps) => {
    return (
        <SafeAreaView
            edges={['top', 'left', 'right']}
            style={{ width: '100%', height }}
        >
            <XStack
                marginVertical="auto"
                paddingHorizontal={16}
                position="relative"
                width="100%"
                alignItems="center"
                justifyContent="space-between"
                gap={10}
            >
                <BackButton
                    fallbackRoute="/roadmap/tasks"
                    iconType={BackButtonIconType.ARROW_LEFT}
                />
                {isScrolled && (
                    <View flexShrink={1}>
                        <LabelRegular textAlign="center" numberOfLines={2}>
                            {title}
                        </LabelRegular>
                    </View>
                )}
                <View width={34}></View>
            </XStack>
        </SafeAreaView>
    );
};

interface GoalDetailsProps {
    goal: IGoal;
}

const GoalDetails = ({ goal }: GoalDetailsProps) => {
    const { trackViewOnce } = useViewEvents();
    trackViewOnce('GoalDetails');

    const insets = useSafeAreaInsets();
    const topNavBarHeight = insets.top + 64;
    const { getColor } = useThemeColors();

    const [fireGoalConfettiCannon, setFireGoalConfettiCannon] = useAtom(
        fireGoalConfettiCannonAtom,
    );

    return (
        <>
            <AnimatedScrollView
                style={{
                    flex: 1,
                    backgroundColor: getColor(
                        GoalColors[
                            goal.category?.description as string
                        ] as ColorTokenKey,
                    ),
                }}
                isHeaderComponentFixed={true}
                isFullScreen={true}
                topBarHeight={topNavBarHeight}
                contentContainerStyle={{ flexGrow: 1 }}
                showsVerticalScrollIndicator={isWeb}
                alwaysBounceVertical={false}
                headerMaxHeight={180}
                HeaderComponent={<Header goalCategory={goal.category} />}
                HeaderNavbarComponent={
                    <TopNavBarComponent
                        height={topNavBarHeight}
                        isScrolled={false}
                    />
                }
                TopNavBarComponent={
                    <TopNavBarComponent
                        height={topNavBarHeight}
                        isScrolled={true}
                        title={goal.description}
                    />
                }
            >
                <PageBelowHeader>
                    <Goal goal={goal} />
                </PageBelowHeader>
            </AnimatedScrollView>
            {fireGoalConfettiCannon && (
                <ConfettiCannon
                    count={50}
                    origin={{ x: 0, y: 0 }}
                    fadeOut={true}
                    fallSpeed={2000}
                    onAnimationEnd={() => {
                        setFireGoalConfettiCannon(false);
                    }}
                />
            )}
        </>
    );
};

export default GoalDetails;
